import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import "./statusMetricBlock.scss";
import useFontScale from "../../../../hooks/useFontScale";

/**
 * StatusMetricBlock Component
 *
 * Displays a metric with a label at the top (light background)
 * and a status-colored block at the bottom with the value.
 * Used for Mobility Metrics etc.
 */
const StatusMetricBlock = ({ label, value, status, icon, className = "" }) => {
  useFontScale();

  // Map status to class name if needed, or pass directly
  const getStatusClass = (s) => {
    if (!s) return "status-default";
    const lowerS = s.toLowerCase();

    // Updated mapping based on TugSideMetricsDetailsV2 logic (poor/average/good) and requested mappings
    if (lowerS === "poor" || lowerS === "high") return "status-poor";
    // "fair" usually aligns with "average" or "moderate" in this context
    if (
      lowerS === "fair" ||
      lowerS === "moderate" ||
      lowerS === "average" ||
      lowerS === "elevated"
    )
      return "status-fair";
    if (lowerS === "good" || lowerS === "low") return "status-good";
    return `status-${lowerS}`; // Fallback for custom statuses
  };

  // Format value based on word count
  const formatValue = (val) => {
    if (!val) return val;
    const str = String(val);
    const words = str.trim().split(/\s+/);

    if (words.length <= 2) {
      // Up to 2 words: capitalize all
      return str.toUpperCase();
    } else {
      // More than 2 words: capitalize only first letter
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }
  };

  const statusClass = getStatusClass(status);

  return (
    <div className={`status-metric-block ${statusClass} ${className}`}>
      <Typography className="metric-block-label" title={label}>
        {label}
      </Typography>
      <div className={`metric-block-value ${statusClass}`}>
        {icon && <span className="metric-block-icon">{icon}</span>}
        {formatValue(value)}
      </div>
    </div>
  );
};

StatusMetricBlock.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  status: PropTypes.string,
  icon: PropTypes.string,
  className: PropTypes.string,
};

export default StatusMetricBlock;
